export const websiteLeadCaptureStyles = ['image_popup', 'compact_popup', 'slide_in', 'sticky_bar'];

export function normalizeLeadCapturePresentation(payload = {}) {
    return {
        presentation_style: websiteLeadCaptureStyles.includes(payload.presentation_style) ? payload.presentation_style : 'image_popup',
        mobile_presentation_style: websiteLeadCaptureStyles.includes(payload.mobile_presentation_style) ? payload.mobile_presentation_style : 'inherit',
        slide_in_position: payload.slide_in_position === 'bottom_left' ? 'bottom_left' : 'bottom_right',
        sticky_bar_position: payload.sticky_bar_position === 'top' ? 'top' : 'bottom',
        compact_message: payload.compact_message || '',
        sticky_bar_text: payload.sticky_bar_text || '',
        sticky_bar_button_label: payload.sticky_bar_button_label || '',
    };
}

export function leadCaptureStyle(payload, mobile = false) {
    const presentation = normalizeLeadCapturePresentation(payload);
    return mobile && presentation.mobile_presentation_style !== 'inherit'
        ? presentation.mobile_presentation_style : presentation.presentation_style;
}

export function leadCaptureIsModal(style, expanded = false) {
    return style === 'image_popup' || style === 'compact_popup' || (style === 'sticky_bar' && expanded);
}

export function leadCaptureClasses(payload, mobile = false, expanded = false) {
    const style = leadCaptureStyle(payload, mobile);
    const presentation = normalizeLeadCapturePresentation(payload);
    return [
        'pono-lead-capture-' + (style === 'sticky_bar' && expanded ? 'compact_popup' : style),
        'pono-lead-capture-' + presentation.slide_in_position,
        'pono-lead-capture-bar-' + presentation.sticky_bar_position,
        {'is-modal': leadCaptureIsModal(style, expanded), 'has-image': !!payload.image_url},
    ];
}

export function leadCaptureVariables(payload, height = null, fonts = {}) {
    const colors = Object.assign({
        overlay_background: 'rgba(15, 23, 42, 0.72)', background: '#0f172a',
        panel_background: '#0b0f19', image_background: '#111827', eyebrow: '#facc15',
        title: '#f8fafc', body: '#d1d5db', bullet: '#f8fafc', bullet_icon: '#fde047',
        input_background: '#111827', input_text: '#f8fafc', input_border: '#475569',
        button_background: '#facc15', button_text: '#111827', fine_print: '#94a3b8', dismiss_text: '#d6bc86',
    }, payload.colors || {});
    // Match the public model's retained default overlay treatment.
    if (/^rgba\(\s*15\s*,\s*23\s*,\s*42\s*,\s*0?\.72(?:0+)?\s*\)$/i.test(colors.overlay_background)) {
        colors.overlay_background = 'rgba(15, 23, 42, 0.78)';
    }
    const variables = {};
    Object.entries(colors).forEach(([key, value]) => { variables['--lc-' + key.replaceAll('_', '-')] = value; });
    if (height) variables['--lc-height'] = height + 'px';
    if (fonts.body) variables['--lc-font'] = fonts.body;
    if (fonts.heading) variables['--lc-heading-font'] = fonts.heading;
    return variables;
}

export const websiteLeadCapturePresentationMethods = {
    websiteLeadCapturePresentationStyle: leadCaptureStyle,
    websiteLeadCapturePresentationClasses: leadCaptureClasses,
    websiteLeadCapturePresentationVariables: leadCaptureVariables,

    websiteLeadCaptureSessionSeen(payload) {
        const key = 'ponoPopupSeen' + payload.id + '=';
        if (this._websiteLeadCaptureSeen?.has(payload.id)) return true;
        try { return document.cookie.split(';').some(value => value.trim().startsWith(key)); } catch (error) { return false; }
    },
    websiteLeadCaptureMarkSessionSeen(payload) {
        this._websiteLeadCaptureSeen ||= new Set();
        this._websiteLeadCaptureSeen.add(payload.id);
        try { document.cookie = 'ponoPopupSeen' + payload.id + '=1; Path=/; SameSite=Lax' + (location.protocol === 'https:' ? '; Secure' : ''); } catch (error) {}
    },
    websiteLeadCaptureBlockedByInteraction() {
        const focused = document.activeElement;
        if (focused?.closest('form') && !focused.closest('.pono-lead-capture-root')) return true;
        return Array.from(document.querySelectorAll('[role="dialog"], [aria-modal="true"], form[aria-busy="true"]'))
            .some(element => !element.closest('.pono-lead-capture-root') && element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');
    },
    websiteLeadCaptureLinkClick(event, kind = 'primary') {
        const state = this.websiteLeadCaptureActiveState();
        if (!state || state.payload.preview) { event.preventDefault(); return; }
        if (kind === 'primary') {
            const data = this.websiteLeadCaptureReadSuppression(state.payload);
            data.clicked_until = Date.now() + Math.max(0, state.payload.click_days) * 86400000;
            this.websiteLeadCaptureWriteSuppression(state.payload, data);
        }
        this.websiteLeadCapturePostEvent(kind === 'primary' ? 'link_click' : 'secondary_link_click');
    },
    websiteLeadCaptureCurrentStyle() {
        const state = this.websiteLeadCaptureActiveState();
        return leadCaptureStyle(state?.payload || {}, this.websiteLeadCaptureViewportWidth < 768);
    },
    websiteLeadCaptureIsBar() {
        return this.websiteLeadCaptureCurrentStyle() === 'sticky_bar' && !this.websiteLeadCaptureExpanded;
    },
    websiteLeadCaptureIsModal() {
        return leadCaptureIsModal(this.websiteLeadCaptureCurrentStyle(), this.websiteLeadCaptureExpanded);
    },
    websiteLeadCaptureActiveMessage() {
        const payload = this.websiteLeadCaptureActiveState()?.payload || {};
        return this.websiteLeadCaptureCurrentStyle() === 'image_popup' ? payload.body : (payload.compact_message || payload.body);
    },
    websiteLeadCaptureHasInlineForm(payload) {
        if (!payload?.submit_url) return false;
        let endpoint;
        try { endpoint = new URL(payload.submit_url, window.location.href); } catch (error) { return false; }
        return Array.from(document.querySelectorAll('form[action]')).some(form => {
            if (form.closest('.pono-lead-capture-root')) return false;
            try {
                const action = new URL(form.getAttribute('action'), window.location.href);
                return action.origin === endpoint.origin && action.pathname.replace(/\/$/, '') === endpoint.pathname.replace(/\/$/, '');
            } catch (error) { return false; }
        });
    },
    websiteLeadCaptureWatchViewport() {
        if (this._websiteLeadCaptureViewportHandler) return;
        this._websiteLeadCaptureViewportHandler = () => {
            const wasBar = this.websiteLeadCaptureIsBar();
            this.websiteLeadCaptureViewportWidth = window.innerWidth;
            this.websiteLeadCaptureViewportHeight = Math.round(window.visualViewport?.height || window.innerHeight);
            if (this.websiteLeadCapturePopupOpen) {
                // An already visible form stays expanded when crossing into the mobile bar style.
                if (!wasBar && this.websiteLeadCaptureCurrentStyle() === 'sticky_bar') this.websiteLeadCaptureExpanded = true;
                this.websiteLeadCaptureSyncPresentation(false);
            }
        };
        this._websiteLeadCaptureViewportHandler();
        window.addEventListener('resize', this._websiteLeadCaptureViewportHandler, {passive: true});
        window.visualViewport?.addEventListener('resize', this._websiteLeadCaptureViewportHandler, {passive: true});
    },
    websiteLeadCaptureUnwatchViewport() {
        if (!this._websiteLeadCaptureViewportHandler) return;
        window.removeEventListener('resize', this._websiteLeadCaptureViewportHandler);
        window.visualViewport?.removeEventListener('resize', this._websiteLeadCaptureViewportHandler);
        this._websiteLeadCaptureViewportHandler = null;
    },
    websiteLeadCaptureSetScrollLock(locked) {
        if (locked && !this._websiteLeadCaptureOwnsScrollLock) {
            this._websiteLeadCaptureScrollWasLocked = document.documentElement.classList.contains('overflow-hidden');
            this._websiteLeadCaptureOwnsScrollLock = true;
            document.documentElement.classList.add('overflow-hidden');
        } else if (!locked && this._websiteLeadCaptureOwnsScrollLock) {
            if (!this._websiteLeadCaptureScrollWasLocked) document.documentElement.classList.remove('overflow-hidden');
            this._websiteLeadCaptureOwnsScrollLock = false;
        }
    },
    websiteLeadCaptureSyncPresentation(focus = false) {
        this.websiteLeadCaptureSetScrollLock(this.websiteLeadCaptureSuccessPopupOpen || (this.websiteLeadCapturePopupOpen && this.websiteLeadCaptureIsModal()));
        this.$nextTick(() => {
            if (!this.websiteLeadCapturePopupOpen || this.websiteLeadCaptureIsBar()) return;
            const form = Array.isArray(this.$refs.websiteLeadCaptureForm) ? this.$refs.websiteLeadCaptureForm[0] : this.$refs.websiteLeadCaptureForm;
            if (form && this.websiteLeadCaptureActiveState()?.payload.action_type !== 'open_link') this.websiteLeadCaptureInitPublicForm(form);
            if (focus && this.websiteLeadCaptureIsModal()) {
                const root = this.$refs.websiteLeadCapturePresentation;
                const target = this.websiteLeadCaptureViewportWidth >= 768
                    ? (form?.querySelector('[data-website-lead-capture-primary-input]') || root?.querySelector('[data-website-lead-capture-link="primary"]')) : root?.querySelector('[data-website-lead-capture-close]');
                (target || root)?.focus({preventScroll: true});
            }
        });
    },
    websiteLeadCaptureExpand() {
        if (!this.websiteLeadCapturePopupOpen || !this.websiteLeadCaptureIsBar()) return;
        this.websiteLeadCaptureExpanded = true;
        this.websiteLeadCaptureSyncPresentation(true);
    },
    websiteLeadCaptureTrapFocus(event) {
        if (!this.websiteLeadCaptureIsModal()) return;
        const root = this.$refs.websiteLeadCapturePresentation;
        const items = Array.from(root?.querySelectorAll('button:not([disabled]), input:not([disabled]), a[href], [tabindex="0"]') || [])
            .filter(element => element.getClientRects().length && element.type !== 'hidden');
        if (!items.length) { event.preventDefault(); root?.focus(); return; }
        const first = items[0], last = items[items.length - 1];
        if (event.shiftKey && (document.activeElement === first || !items.includes(document.activeElement))) {
            event.preventDefault(); last.focus();
        } else if (!event.shiftKey && (document.activeElement === last || !items.includes(document.activeElement))) {
            event.preventDefault(); first.focus();
        }
    },
};
